import { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, FormSelect } from '@/components/ui';
import { Icon } from '@/components/ui/Icon';
import { adminApi, type LivePerformance, type PerformanceAgent } from '@/shared/api/adminApi';
import { toast } from '@/components/ui/Toast';
import { labelFor } from '@/shared/lib/labels';
import { useTicker, elapsedSec, fmtDuration } from '@/shared/hooks/useTicker';
import { AgentActions, type LiveActions } from './AgentActions';
import './live.css';
import { agentStatusLabel, agentsToCsv, fmtHms, hueFromName, initials, pct, statusDuration } from './performance';

const today = () => new Date().toISOString().slice(0, 10);

/** Minimal live-snapshot shape needed to drive the supervisor actions. */
export interface LiveAgentLite {
  id: number;
  on_call: boolean;
  channel_id?: string | null;
  call_id?: number | null;
  call_started_at?: string | null;
  /** Connection state (from the live snapshot, ticked between polls). */
  online?: boolean;
  presence_since?: string | null;
  /**
   * 01/10/2026 — ניטור חי אמיתי: הסטטוס עצמו (זמין/הפסקה…) מגיע מהזרם, לא
   * מהטעינה החד-פעמית של הביצועים; connected = דפדפן חי; line = סופטפון רשום.
   */
  status?: string;
  status_since?: string | null;
  connected?: boolean;
  line?: boolean;
  /** מתי אסטריסק דיווח על מצב הקו. null = עוד לא ידוע (לפני הרישום הבא). */
  line_since?: string | null;
  ringing?: boolean;
  ringing_since?: string | null;
  ringing_from?: string | null;
  call_remote?: string | null;
}

export interface AgentPerformanceProps {
  /** Live snapshot agents (from LiveMonitor's poller) — used to attach actions. */
  liveAgents?: LiveAgentLite[];
  actions?: LiveActions;
  /** Clicking an agent's name opens the per-agent "ביצועי היום" drill-down. */
  onOpenAgent?: (agentId: number, name: string) => void;
  /** Agent to highlight (from a keyword-alert ?listen_agent= deep link). */
  highlightAgentId?: number | null;
  /** Register the row/card DOM node so the deep link can scroll to it. */
  registerRef?: (agentId: number, el: HTMLElement | null) => void;
}

type PerfView = 'table' | 'cards';
const VIEW_KEY = 'merkaziya-perf-view';
function loadView(): PerfView {
  try { return localStorage.getItem(VIEW_KEY) === 'cards' ? 'cards' : 'table'; } catch { return 'table'; }
}

// גווני כחול-כהה מדורגים למשפך — קשיחים גם בעיצוב (GoCall v2), זהים בשני המצבים.
const FUNNEL_BG = [
  'linear-gradient(135deg,#16223A,#1E2D49)',
  'linear-gradient(135deg,#1F3456,#284066)',
  'linear-gradient(135deg,#274770,#30547F)',
  'linear-gradient(135deg,#2F5B89,#386898)',
  'linear-gradient(135deg,#3B6E9E,#4679AA)',
];

interface FunnelStep {
  label: string;
  value: string;
  bg: string;
  badge?: string;
}

/**
 * "ביצועי נציגים" — appended below the live monitor. A chevron KPI funnel plus a
 * sortable per-agent breakdown, over a date range (default today). Read-only.
 */
export function AgentPerformance({ liveAgents, actions, onOpenAgent, highlightAgentId, registerRef }: AgentPerformanceProps = {}) {
  const [from, setFrom] = useState(today());
  const [to, setTo] = useState(today());
  const [agentId, setAgentId] = useState('');
  const [data, setData] = useState<LivePerformance | null>(null);
  const [loading, setLoading] = useState(true);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [view, setView] = useState<PerfView>(loadView);

  function changeView(next: PerfView) {
    setView(next);
    try { localStorage.setItem(VIEW_KEY, next); } catch { /* ignore */ }
  }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await adminApi.livePerformance({ from, to, agent_id: agentId ? Number(agentId) : undefined }));
    } catch {
      toast('שגיאה בטעינת ביצועי נציגים', 'danger');
    } finally {
      setLoading(false);
    }
  }, [from, to, agentId]);

  useEffect(() => { void load(); }, [load]);

  const funnel = data?.funnel;
  const steps: FunnelStep[] = funnel ? [
    { label: 'שיחות יוצאות', value: String(funnel.outbound), bg: FUNNEL_BG[0] },
    { label: 'שיחות שענו', value: String(funnel.answered), bg: FUNNEL_BG[1], badge: `${pct(funnel.answered, funnel.outbound)}%` },
    { label: 'שיחות שנוהלו', value: String(funnel.handled), bg: FUNNEL_BG[2], badge: `${pct(funnel.handled, funnel.answered)}%` },
    // Deal conversion is expressed against answered calls.
    { label: 'עסקאות סגורות', value: String(funnel.deals), bg: FUNNEL_BG[3], badge: `${pct(funnel.deals, funnel.answered)}%` },
    { label: 'סה״כ זמן בשיחה', value: fmtHms(funnel.talk_sec), bg: FUNNEL_BG[4] },
  ] : [];

  // All agents present (unfiltered) for the picker; the filter narrows the fetch.
  const agents = data?.agents ?? [];
  const sortedAgents = useMemo(() => {
    const rows = [...agents];
    rows.sort((a, b) => a.name.localeCompare(b.name, 'he'));
    if (sortDir === 'desc') rows.reverse();
    return rows;
  }, [agents, sortDir]);

  // Cross-reference the live snapshot by agent_id: on-call agents get the
  // supervisor actions (snoop needs channel_id; transfer/takeover need call_id
  // — AgentActions shows whatever is possible) and the prominent live marking.
  const liveById = useMemo(() => {
    const m = new Map<number, LiveAgentLite>();
    (liveAgents ?? []).forEach((l) => m.set(l.id, l));
    return m;
  }, [liveAgents]);
  const canAct = !!actions && !!liveAgents;
  const liveFor = (agentId: number): LiveAgentLite | undefined => {
    const l = liveById.get(agentId);
    return l && l.on_call ? l : undefined;
  };
  // Raw live entry (regardless of on_call) — drives the online/last-seen column.
  const presenceFor = (agentId: number): LiveAgentLite | undefined => liveById.get(agentId);
  // 1s tick drives the live "בשיחה · MM:SS" timers on rows/cards.
  const now = useTicker(1000);

  function exportCsv() {
    const csv = agentsToCsv(sortedAgents);
    // Prepend a BOM so Excel reads the Hebrew as UTF-8.
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `agent-performance-${from}_${to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <Card>
      <div className="stack">
        {/* A. Controls */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 16, fontWeight: 600 }}>ביצועי נציגים</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <button
              type="button"
              className="btn ghost"
              onClick={() => { setFrom(today()); setTo(today()); }}
              title="טווח היום"
            >
              היום
            </button>
            <input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} aria-label="מתאריך"
              style={dateStyle} />
            <input type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} aria-label="עד תאריך"
              style={dateStyle} />
            <FormSelect value={agentId} onChange={(e) => setAgentId(e.target.value)} aria-label="סינון נציג" style={{ width: 180 }}>
              <option value="">כל הנציגים</option>
              {agents.map((a) => <option key={a.agent_id} value={a.agent_id}>{a.name}</option>)}
            </FormSelect>
            {/* View toggle: table (default) / cards. */}
            <div className="seg" role="tablist" aria-label="תצוגת נציגים">
              <button type="button" role="tab" aria-selected={view === 'table'} onClick={() => changeView('table')}>
                טבלה
              </button>
              <button type="button" role="tab" aria-selected={view === 'cards'} onClick={() => changeView('cards')}>
                כרטיסים
              </button>
            </div>
            <button type="button" className="btn" onClick={exportCsv} disabled={sortedAgents.length === 0}>
              <Icon name="download" size={14} /> ייצוא CSV
            </button>
          </div>
        </div>

        {/* B. Chevron funnel */}
        {loading ? (
          <div style={{ color: 'var(--muted)' }}>טוען…</div>
        ) : !funnel ? (
          <div style={{ color: 'var(--muted)' }}>אין נתונים לטווח הנבחר.</div>
        ) : (
          <>
            {/* clip-path חותך box-shadow רגיל — ההצללה יושבת על הקונטיינר. */}
            <div dir="rtl" style={{ display: 'flex', alignItems: 'stretch', overflowX: 'auto', paddingBottom: 4, filter: 'drop-shadow(0 10px 18px rgba(20,45,80,.14))' }}>
              {steps.map((s, i) => (
                <ChevronStep key={i} step={s} index={i} first={i === 0} last={i === steps.length - 1} />
              ))}
            </div>

            {/* C. Agent list — table or cards */}
            {view === 'cards' ? (
              <AgentCards agents={sortedAgents} actions={actions} liveFor={liveFor} presenceFor={presenceFor} canAct={canAct} now={now}
                onOpenAgent={onOpenAgent} highlightAgentId={highlightAgentId} registerRef={registerRef} />
            ) : (
            <div style={{ overflowX: 'auto', borderRadius: 12, border: '1px solid var(--glass-line-2)' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5 }}>
                <thead>
                  <tr style={{ background: 'var(--surface-3)' }}>
                    <th style={thStyle('start')}>
                      <button type="button" onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', font: 'inherit', color: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        שם מלא <Icon name="chevronD" size={12} style={{ transform: sortDir === 'asc' ? undefined : 'rotate(180deg)' }} />
                      </button>
                    </th>
                    <th style={thStyle('start')}>סטטוס</th>
                    <th style={thStyle('start')}>חיבור</th>
                    <th style={thStyle('center')}>שיחות יוצאות</th>
                    <th style={thStyle('center')}>נוהלו</th>
                    <th style={thStyle('center')}>סגורות</th>
                    <th style={thStyle('center')}>זמן בחיוג</th>
                    <th style={thStyle('center')}>זמן בשיחה</th>
                    <th style={thStyle('center')}>חיוג + שיחה</th>
                    {canAct && <th style={thStyle('center')}>פעולות</th>}
                  </tr>
                </thead>
                <tbody>
                  {sortedAgents.length === 0 ? (
                    <tr><td colSpan={canAct ? 10 : 9} style={{ padding: 12, color: 'var(--muted)', textAlign: 'center' }}>אין נציגים בטווח הנבחר.</td></tr>
                  ) : sortedAgents.map((a, i) => (
                    <AgentRow
                      index={i}
                      key={a.agent_id}
                      a={a}
                      canAct={canAct}
                      live={liveFor(a.agent_id)}
                      presence={presenceFor(a.agent_id)}
                      actions={actions}
                      onOpenAgent={onOpenAgent}
                      highlighted={highlightAgentId != null && highlightAgentId === a.agent_id}
                      registerRef={registerRef}
                      now={now}
                    />
                  ))}
                </tbody>
              </table>
            </div>
            )}
          </>
        )}
      </div>
    </Card>
  );
}

/**
 * Live "connected / last-seen" indicator. Driven by the SSE snapshot's `online`
 * + `presence_since`, ticked every second so it updates in real time — an agent
 * who connects or drops changes here on the next poll, and the timer keeps
 * counting between polls.
 */
function ConnectionCell({ presence, now, stacked }: { presence?: LiveAgentLite; now: number; stacked?: boolean }) {
  // No live entry yet (agent not in this snapshot) — treat as offline/unknown.
  const online = presence?.connected ?? presence?.online ?? false;
  const since = presence?.presence_since ?? null;
  // מחובר אבל הסופטפון לא רשום באסטריסק — לא יקבל שיחות. רק כשאסטריסק
  // באמת דיווח (line_since): שורה בלי דיווח עדיין (נציג שלא נרשם מחדש מאז
  // הפריסה) היא "לא ידוע", לא "מנותק".
  const noLine = online && presence?.line === false && !!presence?.line_since;
  // בטבלה: הטיימר / "נראה לפני" בשורה שנייה קטנה, כדי לצמצם את רוחב העמודה.
  if (stacked) {
    const color = online ? 'var(--ok)' : 'var(--danger)';
    return (
      <div style={{ lineHeight: 1.3 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: online ? 'var(--text-2)' : 'var(--danger)', whiteSpace: 'nowrap' }}>
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: color, animation: online ? undefined : 'gcBlink 1.2s ease-in-out infinite' }} />
          {online ? 'מחובר' : 'מנותק'}
        </span>
        {since && (
          <div style={{ fontSize: 11.5, color: 'var(--muted)', whiteSpace: 'nowrap', marginTop: 2 }}>
            {online
              ? <span dir="ltr" style={{ fontFamily: 'var(--font-mono)' }}>{fmtDuration(elapsedSec(since, now))}</span>
              : `נראה לפני ${fmtAgo(elapsedSec(since, now))}`}
          </div>
        )}
        {noLine && (
          <div style={{ fontSize: 11.5, color: 'var(--warn)', whiteSpace: 'nowrap', marginTop: 2 }} title="הדפדפן פתוח אבל הסופטפון לא רשום — שיחות לא יגיעו אליו">
            הסופטפון לא מחובר
          </div>
        )}
      </div>
    );
  }
  if (online) {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'var(--text-2)', whiteSpace: 'nowrap' }}>
        <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--ok)' }} />
        מחובר{since ? <span dir="ltr" style={{ fontFamily: 'var(--font-mono)', color: 'var(--muted)' }}>{fmtDuration(elapsedSec(since, now))}</span> : ''}
        {noLine && <span style={{ color: 'var(--warn)' }}>· בלי סופטפון</span>}
      </span>
    );
  }
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'var(--danger)', whiteSpace: 'nowrap' }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--danger)', animation: 'gcBlink 1.2s ease-in-out infinite' }} />
      מנותק{since ? ` · נראה לפני ${fmtAgo(elapsedSec(since, now))}` : ''}
    </span>
  );
}

/** קיצור "לפני X" בעברית מתוך שניות שחלפו (חי). */
function fmtAgo(sec: number): string {
  if (sec < 60) return 'רגע';
  if (sec < 3600) return `${Math.floor(sec / 60)} דק׳`;
  if (sec < 86400) return `${Math.floor(sec / 3600)} שע׳`;
  return `${Math.floor(sec / 86400)} ימים`;
}

function AgentRow({ a, index, canAct, live, presence, actions, onOpenAgent, highlighted, registerRef, now }: {
  a: PerformanceAgent;
  index: number;
  canAct: boolean;
  live?: LiveAgentLite;
  presence?: LiveAgentLite;
  actions?: LiveActions;
  onOpenAgent?: (agentId: number, name: string) => void;
  highlighted?: boolean;
  registerRef?: (agentId: number, el: HTMLElement | null) => void;
  now?: number;
}) {
  // The live snapshot beats the (range-scoped) performance fetch — status,
  // on-call and ringing update every SSE tick; the fetch is only a fallback.
  const st = liveState(a, presence, now);
  const { onCallNow, ringing, status } = st;
  const dur = statusDuration(st.statusSince, now);
  const callDur = st.callDur;
  const online = st.online;
  const tag = statusTag(onCallNow ? 'oncall' : ringing ? 'ringing' : status);
  const td: React.CSSProperties = { padding: '10px 8px', textAlign: 'center', fontVariantNumeric: 'tabular-nums' };
  const mono: React.CSSProperties = { ...td, fontFamily: 'var(--font-mono)', fontSize: 12, whiteSpace: 'nowrap' };
  return (
    <tr
      ref={registerRef ? (el) => registerRef(a.agent_id, el) : undefined}
      style={{
        borderTop: `1px solid ${highlighted ? 'var(--warn)' : 'var(--glass-line-2)'}`,
        outline: highlighted ? '2px solid var(--warn)' : undefined,
        background: onCallNow ? 'var(--accent-soft)' : undefined,
        // נכנסת פעם אחת ב-mount (0.1+i*0.035); עדכון חי לא מריץ מחדש — המפתח יציב.
        animation: `gcRow .45s var(--ease-out) ${0.1 + index * 0.035}s backwards`,
      }}
      className="hover-row"
    >
      <td style={{ padding: '10px 8px', textAlign: 'start' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          <AgentAvatar name={a.name} ring={onCallNow} dot={onCallNow ? 'var(--accent)' : online ? 'var(--ok)' : 'var(--danger)'} />
          <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, lineHeight: 1.3 }}>
            {onOpenAgent ? (
              <button type="button" onClick={() => onOpenAgent(a.agent_id, a.name)}
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', fontWeight: 600, color: 'var(--text)', textAlign: 'start', whiteSpace: 'nowrap' }}>
                {a.name}
              </button>
            ) : <span style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{a.name}</span>}
            <span style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', whiteSpace: 'nowrap' }}>
              {labelFor('role', a.role)}{a.extension ? ` · שלוחה ${a.extension}` : ''}
            </span>
          </div>
        </div>
      </td>
      <td style={{ padding: '10px 8px', textAlign: 'start' }}>
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap',
          fontSize: 12, fontWeight: 600, padding: '2px 10px', borderRadius: 'var(--r-pill)',
          background: tag.bg, color: tag.fg,
        }}>
          {onCallNow ? <EqBars /> : <span className={ringing ? 'live-dot' : undefined} style={{ width: 6, height: 6, borderRadius: 'var(--r-pill)', background: 'currentColor' }} />}
          {onCallNow ? 'בשיחה' : ringing ? 'מצלצל' : agentStatusLabel(status)}
          {onCallNow && callDur && <span dir="ltr" style={{ fontVariantNumeric: 'tabular-nums' }}>{callDur}</span>}
          {ringing && st.ringDur && <span dir="ltr" style={{ fontVariantNumeric: 'tabular-nums' }}>{st.ringDur}</span>}
        </span>
        {/* משך הסטטוס / מספר הצד השני בשורה שנייה — שומר על עמודה צרה */}
        {(onCallNow || ringing) && st.remote ? (
          <div dir="ltr" style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 3, whiteSpace: 'nowrap', textAlign: 'end' }}>{st.remote}</div>
        ) : !(onCallNow && callDur) && dur && (
          <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 3, whiteSpace: 'nowrap' }}>{dur}</div>
        )}
      </td>
      <td style={{ padding: '10px 8px', textAlign: 'start' }}>
        <ConnectionCell presence={presence} now={now ?? 0} stacked />
      </td>
      <td style={td}>
        {a.outbound} <span style={{ color: 'var(--muted)', fontSize: 12 }}>({a.outbound_pct}%)</span>
        {a.manual > 0 && <span style={{ color: 'var(--ok)', fontSize: 12 }}> (ידני: {a.manual})</span>}
      </td>
      <td style={td}>{a.handled}</td>
      <td style={{ ...td, fontWeight: 600, color: 'var(--ok)' }}>{a.deals}</td>
      <td style={mono} dir="ltr">{fmtHms(a.dialing_sec)}</td>
      <td style={mono} dir="ltr">{fmtHms(a.talk_sec)}</td>
      <td style={{ ...mono, fontWeight: 600 }} dir="ltr">{fmtHms(a.combined_sec)}</td>
      {canAct && (
        <td style={{ padding: '10px 8px', textAlign: 'center' }}>
          {live && actions ? (
            <div style={{ display: 'inline-flex', minWidth: 'max-content' }}>
              <AgentActions compact agent={{ id: a.agent_id, name: a.name, call_id: live.call_id, channel_id: live.channel_id }} {...actions} />
            </div>
          ) : (
            <span style={{ color: 'var(--faint)', fontSize: 'var(--t-xs)' }}>—</span>
          )}
        </td>
      )}
    </tr>
  );
}

/**
 * חץ אחד ב-funnel — צבוע במלואו בצבע השלב, טקסט לבן, והחיצים משתלבים זה
 * בזה (clip-path + חפיפה שלילית). הקונטיינר תמיד RTL כך שהקואורדינטות
 * הפיזיות של הפוליגונים נכונות: הראשון מימין עם שפה ישרה, כל חץ מחודד
 * שמאלה ונכנס למגרעת של הבא אחריו.
 */
const CHEV_NOTCH = 16; // עומק החוד/המגרעת (px)
const CHEV_OVERLAP = 10; // כמה כל חץ נבלע בקודם

function ChevronStep({ step, index, first, last }: { step: FunnelStep; index: number; first: boolean; last: boolean }) {
  const n = `${CHEV_NOTCH}px`;
  // קואורדינטות פיזיות (השורה dir=rtl): ימין = תחילת הזרימה.
  const right = first ? '100% 0, 100% 100%' : `100% 0, calc(100% - ${n}) 50%, 100% 100%`;
  const left = last ? '0 100%, 0 0' : `${n} 100%, 0 50%, ${n} 0`;

  return (
    <div
      className="lm-chev"
      style={{
        // כניסה מדורגת (העיצוב: gcUp .55s, השהיה i*0.07)
        animation: `gcUp .55s var(--ease-out) ${index * 0.07}s backwards`,
        position: 'relative',
        flex: '1 0 150px',
        minWidth: 150,
        background: step.bg,
        color: '#fff',
        // המגרעת בצד ימין "אוכלת" שטח — מרפדים כך שהכיתוב לא ייחתך.
        padding: `14px ${first ? 18 : CHEV_NOTCH + 14}px 14px ${last ? 18 : CHEV_NOTCH + 10}px`,
        clipPath: `polygon(${right}, ${left})`,
        marginInlineEnd: last ? 0 : -CHEV_OVERLAP,
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
      }}
    >
      <div aria-hidden style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg,rgba(255,255,255,.28),rgba(255,255,255,0) 55%)', pointerEvents: 'none' }} />
      <span style={{ position: 'relative', fontSize: 12.5, fontWeight: 500, lineHeight: 1.3, whiteSpace: 'nowrap' }}>{step.label}</span>
      <div style={{ position: 'relative', display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
        <span dir="ltr" style={{ fontSize: 24, fontWeight: 600, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>{step.value}</span>
        {step.badge && (
          <span style={{ fontSize: 11, fontWeight: 600, padding: '1px 7px', borderRadius: 'var(--r-pill)', background: 'rgba(255,255,255,.24)' }}>
            {step.badge}
          </span>
        )}
      </div>
    </div>
  );
}

function thStyle(align: 'start' | 'center'): React.CSSProperties {
  return { padding: '10px 8px', textAlign: align, fontSize: 12, color: 'var(--muted)', whiteSpace: 'nowrap', fontWeight: 500 };
}

const dateStyle: React.CSSProperties = {
  height: 36, padding: '0 10px', border: '1px solid var(--glass-line-2)', borderRadius: 'var(--r-2)',
  background: 'var(--glass-soft)', color: 'var(--text)', fontFamily: 'inherit', fontSize: 'var(--t-sm)',
};

/** תג הסטטוס (רקע רך + צבע) — מיפוי הסטטוסים לגוונים כמו בעיצוב. */
const STATUS_TAG: Record<string, { bg: string; fg: string }> = {
  available: { bg: 'var(--ok-soft)', fg: 'var(--ok)' },
  oncall: { bg: 'var(--accent-soft)', fg: 'var(--accent-ink)' },
  acw: { bg: 'var(--surface-3)', fg: 'var(--busy)' },
  break: { bg: 'var(--warn-soft)', fg: 'var(--warn)' },
  lunch: { bg: 'var(--warn-soft)', fg: 'var(--warn)' },
  training: { bg: 'var(--surface-3)', fg: 'var(--text-2)' },
  offline: { bg: 'var(--danger-soft)', fg: 'var(--danger)' },
  ringing: { bg: 'var(--warn-soft)', fg: 'var(--warn)' },
};

/**
 * מצב חי של נציג — הזרם גובר; הטעינה החד-פעמית של הביצועים רק כשאין לו שורה
 * בזרם עדיין. קודם הסטטוס (זמין/הפסקה) נלקח מהטעינה ונשאר קפוא עד רענון.
 */
function liveState(a: PerformanceAgent, live: LiveAgentLite | undefined, now?: number) {
  const status = live?.status ?? a.status;
  const statusSince = live ? (live.status_since ?? null) : a.status_since;
  const onCallNow = live ? live.on_call : a.status === 'oncall';
  const ringing = !onCallNow && !!live?.ringing;
  const t = now ?? Date.now();
  return {
    status,
    statusSince,
    onCallNow,
    ringing,
    online: live?.connected ?? live?.online ?? false,
    callDur: onCallNow && live?.call_started_at ? fmtDuration(elapsedSec(live.call_started_at, t)) : null,
    ringDur: ringing && live?.ringing_since ? fmtDuration(elapsedSec(live.ringing_since, t)) : null,
    remote: onCallNow ? (live?.call_remote ?? null) : ringing ? (live?.ringing_from ?? null) : null,
  };
}
function statusTag(status: string) {
  return STATUS_TAG[status] ?? { bg: 'var(--surface-3)', fg: 'var(--text-2)' };
}

/** פס "אקולייזר" קטן שמסמן שיחה פעילה בתג הסטטוס. */
function EqBars() {
  return <span className="eq" aria-hidden><i /><i /><i /></span>;
}

/** אווטאר עגול עם ראשי תיבות ונקודת חיבור בפינה. */
function AgentAvatar({ name, dot, ring, size = 32 }: { name: string; dot: string; ring?: boolean; size?: number }) {
  return (
    <span className={ring ? 'anim-ring' : undefined} style={{
      position: 'relative', width: size, height: size, borderRadius: 'var(--r-pill)', flex: 'none',
      display: 'grid', placeItems: 'center', fontSize: size > 34 ? 13 : 12, fontWeight: 600,
      color: 'var(--on-accent)', background: `oklch(0.55 0.06 ${hueFromName(name)})`,
    }}>
      {initials(name)}
      <span style={{
        position: 'absolute', bottom: -1, insetInlineEnd: -1, width: 10, height: 10,
        borderRadius: 'var(--r-pill)', border: '2px solid var(--surface)', background: dot,
      }} />
    </span>
  );
}

/** Responsive card grid — one card per agent, surfacing the same stats as the table. */
function AgentCards({ agents, actions, liveFor, presenceFor, canAct, onOpenAgent, highlightAgentId, registerRef, now }: {
  agents: PerformanceAgent[];
  actions?: LiveActions;
  liveFor: (agentId: number) => LiveAgentLite | undefined;
  presenceFor: (agentId: number) => LiveAgentLite | undefined;
  canAct: boolean;
  onOpenAgent?: (agentId: number, name: string) => void;
  highlightAgentId?: number | null;
  registerRef?: (agentId: number, el: HTMLElement | null) => void;
  now?: number;
}) {
  if (agents.length === 0) {
    return <div style={{ padding: 12, color: 'var(--muted)', textAlign: 'center' }}>אין נציגים בטווח הנבחר.</div>;
  }
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 12 }}>
      {agents.map((a, i) => (
        <AgentCard
          index={i}
          key={a.agent_id}
          a={a}
          canAct={canAct}
          live={liveFor(a.agent_id)}
          presence={presenceFor(a.agent_id)}
          actions={actions}
          onOpenAgent={onOpenAgent}
          highlighted={highlightAgentId != null && highlightAgentId === a.agent_id}
          registerRef={registerRef}
          now={now}
        />
      ))}
    </div>
  );
}

function AgentCard({ a, index, canAct, live, presence, actions, onOpenAgent, highlighted, registerRef, now }: {
  a: PerformanceAgent;
  index: number;
  canAct: boolean;
  live?: LiveAgentLite;
  presence?: LiveAgentLite;
  actions?: LiveActions;
  onOpenAgent?: (agentId: number, name: string) => void;
  highlighted?: boolean;
  registerRef?: (agentId: number, el: HTMLElement | null) => void;
  now?: number;
}) {
  // Live snapshot wins over the range-scoped fetch (status, call, ringing).
  const st = liveState(a, presence, now);
  const isOncall = st.onCallNow;
  const ringing = st.ringing;
  const dur = statusDuration(st.statusSince, now);
  const callDur = st.callDur;
  // The avatar presence dot reflects real connection state from the live snapshot.
  const online = st.online;
  const dotColor = isOncall ? 'var(--accent)' : online ? 'var(--ok)' : 'var(--danger)';
  const tag = statusTag(isOncall ? 'oncall' : ringing ? 'ringing' : st.status);
  return (
    <div
      className="lm-agent-card"
      ref={registerRef ? (el) => registerRef(a.agent_id, el) : undefined}
      style={{
        padding: 16, display: 'flex', flexDirection: 'column', gap: 12,
        animation: `gcUp .5s var(--ease-out) ${0.1 + index * 0.035}s backwards`,
        background: isOncall ? 'var(--accent-soft)' : 'var(--glass-soft)',
        border: highlighted
          ? '2px solid var(--warn)'
          : isOncall
            ? '1px solid var(--accent)'
            : '1px solid var(--glass-line)',
        borderRadius: 'var(--r-3)',
      }}
    >
      {/* אווטאר + שם + שלוחה/תפקיד + חיבור */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <AgentAvatar name={a.name} ring={isOncall} dot={dotColor} size={38} />
        <div style={{ flex: 1, minWidth: 0, lineHeight: 1.3 }}>
          <button type="button" title="פתח ביצועי היום" onClick={onOpenAgent ? () => onOpenAgent(a.agent_id, a.name) : undefined}
            style={{ background: 'none', border: 'none', padding: 0, cursor: onOpenAgent ? 'pointer' : 'default', font: 'inherit', fontWeight: 600, color: 'var(--text)', display: 'block', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {a.name}
          </button>
          <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>
            {a.extension ? `ext ${a.extension}` : labelFor('role', a.role)}
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600,
          borderRadius: 'var(--r-pill)', padding: '3px 10px', background: tag.bg, color: tag.fg,
        }}>
          {isOncall ? <EqBars /> : <span className={ringing ? 'live-dot' : undefined} style={{ width: 6, height: 6, borderRadius: '50%', background: 'currentColor' }} />}
          {isOncall ? 'בשיחה' : ringing ? 'מצלצל' : agentStatusLabel(st.status)}
          {isOncall && callDur
            ? <span dir="ltr" style={{ fontVariantNumeric: 'tabular-nums' }}>{callDur}</span>
            : ringing && st.ringDur
              ? <span dir="ltr" style={{ fontVariantNumeric: 'tabular-nums' }}>{st.ringDur}</span>
              : dur ? ` · ${dur}` : ''}
        </span>
        <ConnectionCell presence={presence} now={now ?? 0} />
      </div>

      {/* שלושה מונים עיקריים כמו בעיצוב + שורת משנה */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6, textAlign: 'center' }}>
        <MiniBox label="יוצאות" value={a.outbound} />
        <MiniBox label="נוהלו" value={a.handled} />
        <MiniBox label="עסקאות" value={a.deals} ok />
      </div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <Stat label="ידני" value={String(a.manual)} />
        <Stat label="זמן שיחה" value={fmtHms(a.talk_sec)} />
      </div>

      {/* Live supervisor actions — only for an agent currently on a call. */}
      {canAct && live && actions && (
        <div style={{ borderTop: '1px solid var(--glass-line-2)', paddingTop: 10 }}>
          <AgentActions agent={{ id: a.agent_id, name: a.name, call_id: live.call_id, channel_id: live.channel_id }} {...actions} />
        </div>
      )}
    </div>
  );
}

function MiniBox({ label, value, ok }: { label: string; value: number; ok?: boolean }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', padding: 6, borderRadius: 7, background: ok ? 'var(--ok-soft)' : 'var(--surface-3)' }}>
      <span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: ok ? 'var(--ok)' : 'var(--text)' }}>{value}</span>
      <span style={{ fontSize: 11, color: 'var(--muted)' }}>{label}</span>
    </div>
  );
}

function Stat({ label, value, span }: { label: string; value: string; span?: number }) {
  return (
    <span style={{ fontSize: 'var(--t-xs)', gridColumn: span ? `span ${span}` : undefined, whiteSpace: 'nowrap' }}>
      <span style={{ color: 'var(--muted)' }}>{label} </span>
      <span dir="ltr" style={{ color: 'var(--text)', fontWeight: 700 }}>{value}</span>
    </span>
  );
}
